import {
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  TemplateHint,
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { TemplateNodesDict, TemplatePagesDict } from 'src/exShared/types/types'
import { EditTemplateState } from 'src/state/ducks/templates/editTemplate/reducer'
import { TemplatePagesState } from 'src/state/ducks/templates/templatePages/reducer'
import { ValidationResult, err, ok } from './types'
import { validatePageHasQuestion as validatePageHasQuestionOriginal } from './validate'

const TEMPLATE_NAME_MAX_LENGTH = 255
const HINT_NAME_MAX_LENGTH = 255
const PAGE_NAME_MAX_LENGTH = 255

/**
 * ひな形名が空の場合はエラー
 */
export const validateTemplateName = (
  templateData: EditTemplateState,
): ValidationResult => {
  if (templateData.name === '') {
    return err({ type: 'template', message: 'ひな形名を入力してください' })
  }
  if ([...templateData.name].length > TEMPLATE_NAME_MAX_LENGTH) {
    return err({
      type: 'template',
      message: `ひな形名は${TEMPLATE_NAME_MAX_LENGTH}文字以内で入力してください`,
    })
  }
  return ok()
}

/**
 * ヒントが存在していてヒント名が未入力の場合はエラー
 */
export const validateTemplateHints = (
  templateHints: TemplateHint[],
): ValidationResult => {
  const hintsExist = templateHints.length !== 0
  const allHintsHaveName = templateHints.every(hint => hint.name !== '')

  if (hintsExist && !allHintsHaveName) {
    return err({ type: 'hint', message: 'ヒント名を入力してください' })
  }
  const tooLongHint = templateHints.find(
    hint => [...hint.name].length > HINT_NAME_MAX_LENGTH,
  )
  if (tooLongHint) {
    return err({
      type: 'hint',
      message: `ヒント名は${HINT_NAME_MAX_LENGTH}文字以内で入力してください`,
    })
  }
  return ok()
}

/**
 * Excel変換が有効で、Excel変換ラベルが1つも設定されていない場合はエラー
 */
export const validateExcelConversionLabel = (
  templateData: EditTemplateState,
  templateNodes: TemplateNodeSchema[],
): ValidationResult => {
  if (!templateData.isExcelConversion) {
    return ok()
  }

  const isSetExcelConversionLabel = templateNodes.some(templateNode => {
    if (templateNode.type === TemplateNodeTypeEnum.Question) {
      return (templateNode?.question?.excelConversionTypes ?? []).length > 0
    }
    return false
  })

  if (!isSetExcelConversionLabel) {
    return err({
      type: 'page',
      message: '少なくとも1つExcel変換ラベルを設定してください',
    })
  }
  return ok()
}

/**
 * ページが1つも存在しない場合はエラー
 */
export const validateTemplatePages = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  if (templatePagesData.templatePageIds.length === 0) {
    return err({
      type: 'page',
      message: '少なくとも1つページを設定してください',
    })
  }
  return ok()
}

/**
 * ページごとの質問存在チェック
 * 全てのページに少なくとも1つの質問がない場合はエラー
 */
export const validatePageHasQuestion = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const templateNodesDict: TemplateNodesDict = templatePagesData.templateNodes
  const templatePagesDict: TemplatePagesDict = templatePagesData.templatePages
  const everyPageHasSomeQuestion = validatePageHasQuestionOriginal(
    templateNodesDict,
    templatePagesDict,
  )

  if (!everyPageHasSomeQuestion) {
    return err({ type: 'page', message: '少なくとも1つ質問を設定してください' })
  }
  return ok()
}

/**
 * レポート名設定のバリデーションで使用可能な質問ノードを取得
 * 繰り返しセクション配下のノードは除外される
 */
const getTextQuestionNodes = (
  templateNodes: TemplateNodeSchema[],
  exceptionNodeIds: number[],
): TemplateNodeSchema[] => {
  return templateNodes.filter(
    node =>
      node.type === TemplateNodeTypeEnum.Question &&
      node.question?.responseType === ResponseTypeEnum.TEXT &&
      node.id &&
      !exceptionNodeIds.includes(node.id) &&
      node.question?.name &&
      node.question?.responseTexts?.[0]?.subType ===
        ResponseTextSubTypeEnum.SINGLE_LINE,
  )
}

/**
 * 例外となる繰り返しセクション配下のノードIDを取得
 */
const getExceptionQuestionNodeUuIds = (
  templateNodes: TemplateNodeSchema[],
): number[] => {
  const ids: number[] = []
  const traverseNodes = (nodes?: number[]) => {
    if (!nodes) return
    nodes.forEach(nodeId => {
      const targetNode = templateNodes.find(node => node.id === nodeId)
      if (!targetNode) return
      ids.push(targetNode.id)
      if (targetNode.nodes.length > 0) {
        traverseNodes(targetNode.nodes)
      }
    })
  }

  templateNodes
    .filter(
      node =>
        node.type === TemplateNodeTypeEnum.Section && node.section?.isRepeat,
    )
    .forEach(section => traverseNodes(section.nodes))

  return ids
}

/**
 * レポート名設定のバリデーション
 * - レポート名の設定が有効で、質問が設定されていない場合はエラー
 * - レポート名の設定に選択されている質問が有効でない場合はエラー
 */
export const validateReportNamingRules = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const templateNodes = Object.values(templatePagesData.templateNodes)

  if (!templatePagesData.isReportNamingRulesEnabled) {
    return ok()
  }

  if (templatePagesData.templateReportNamingRulesTemplateNodeUuid === '') {
    return err({ type: 'reportName', message: '質問を選択してください' })
  }

  const exceptionNodeIds = getExceptionQuestionNodeUuIds(templateNodes)
  const textQuestionNodes = getTextQuestionNodes(
    templateNodes,
    exceptionNodeIds,
  )

  if (
    !textQuestionNodes.find(
      node =>
        node.uuid ===
        templatePagesData.templateReportNamingRulesTemplateNodeUuid,
    )
  ) {
    return err({
      type: 'reportName',
      message: 'レポート名の設定に設定する質問を選択し直してください',
    })
  }

  return ok()
}

/**
 * 製品マスタと紐づける設定のバリデーション
 * 「製品マスタと紐づける」がONの時に製品マスタが指定されていない場合はエラー
 * @param templateData - テンプレートデータ
 */
export const validateMasterSelection = (
  templateData: EditTemplateState,
): ValidationResult => {
  // 「製品マスタと紐づける」がONで、製品マスタが指定されていない場合はエラー
  if (templateData.isMasterEnabled && !templateData.masterUuid) {
    return err({
      type: 'master',
      message: '製品マスタを選択してください',
    })
  }

  return ok()
}

/**
 * ページ名の文字数バリデーション
 * ページ名が255文字を超える場合はエラー
 */
export const validatePageNameLength = (
  templatePagesData: TemplatePagesState,
): ValidationResult => {
  const pages = Object.values(templatePagesData.templatePages)
  for (const page of pages) {
    const name = page.name ?? ''
    if ([...name].length > PAGE_NAME_MAX_LENGTH) {
      return err({
        type: 'page',
        message: `ページ名が${PAGE_NAME_MAX_LENGTH}文字を超えているページがあります。${PAGE_NAME_MAX_LENGTH}文字以内で入力してください`,
      })
    }
  }
  return ok()
}
